import React, { Fragment, useRef } from 'react';
import findIndex from 'lodash-es/findIndex';
import { Menu } from '@teetsh/app/src/components/Menu';
import {
  EllipsisVerticalIcon as DotsVertical,
  PlusIcon as Plus,
  TrashIcon as Trash,
} from '@heroicons/react/24/outline';
import { DuplicateIcon } from 'components/Icons/icons/Duplicate';
import {
  removeItemAtIndex,
  replaceItemAtIndex,
} from '@teetsh/app/src/utils/listMethods';
import tw from 'twin.macro';
import _flatMap from 'lodash-es/flatMap';
import { CompetenceNumerique } from '@teetsh/app/src/containers/Livrets/types/livret';
import Buttons from '@teetsh/app/src/components/Form/ButtonsRadio';
import { useUpdateLivretEvalCompNumerique } from '@teetsh/app/src/containers/Livrets/queries/useUpdateLivretEvalCompNumerique';
import { useTranslation } from 'react-i18next';

interface Props {
  value: CompetenceNumerique[];
  onChange?: (p: CompetenceNumerique[]) => void;
  print?: boolean;
  livretEvalId: string;
}

interface CompetenceNumeriqueNomenclature {
  code: string;
  libelle: string;
}

interface DomaineCompetencesNumeriquesNomenclature {
  libelle: string;
  competencesNumerique: CompetenceNumeriqueNomenclature[];
}

const allDomaines: DomaineCompetencesNumeriquesNomenclature[] = [
  {
    libelle: 'Information et données',
    competencesNumerique: [
      {
        code: 'CN_INF_MEN',
        libelle: "Mener une recherche et une veille d'information",
      },
      { code: 'CN_INF_GER', libelle: 'Gérer des données' },
      { code: 'CN_INF_TRA', libelle: 'Traiter des données' },
    ],
  },
  {
    libelle: 'Communication et collaboration',
    competencesNumerique: [
      { code: 'CN_COM_INT', libelle: 'Interagir' },
      { code: 'CN_COM_PAR', libelle: 'Partager et publier' },
      { code: 'CN_COM_COL', libelle: 'Collaborer' },
      { code: 'CN_COM_SIN', libelle: "S'insérer dans le monde numérique" },
    ],
  },
  {
    libelle: 'Création de contenus',
    competencesNumerique: [
      { code: 'CN_CRE_TEX', libelle: 'Développer des documents textuels' },
      { code: 'CN_CRE_MUL', libelle: 'Développer des documents multimédia' },
      { code: 'CN_CRE_ADA', libelle: 'Adapter les documents à leur finalité' },
      { code: 'CN_CRE_PRO', libelle: 'Programmer' },
    ],
  },
  {
    libelle: 'Protection et sécurité',
    competencesNumerique: [
      { code: 'CN_PRO_SEC', libelle: "Sécuriser l'environnement numérique" },
      {
        code: 'CN_PRO_DON',
        libelle: 'Protéger les données personnelles et la vie privée',
      },
      {
        code: 'CN_PRO_SAN',
        libelle: "Protéger la santé, le bien-être et l'environnement",
      },
    ],
  },
  {
    libelle: 'Environnement numérique',
    competencesNumerique: [
      { code: 'CN_ENV_RES', libelle: 'Résoudre des problèmes techniques' },
      {
        code: 'CN_ENV_EVO',
        libelle: 'Évoluer dans un environnement numérique',
      },
    ],
  },
];

function getAllCompetencesNumeriques() {
  return _flatMap(allDomaines, d => d.competencesNumerique);
}

function getRemainingParcoursNomenclature(
  competences: CompetenceNumerique[],
): DomaineCompetencesNumeriquesNomenclature[] {
  return allDomaines.reduce((acc, num) => {
    const remaining = num.competencesNumerique.filter(
      c => !competences.some(cn => cn.code === c.code),
    );
    if (remaining.length > 0) {
      return [
        ...acc,
        {
          libelle: num.libelle,
          competencesNumerique: remaining,
        },
      ];
    }
    return acc;
  }, []);
}

interface ValueDomaine {
  domaine: string;
  competences: CompetenceNumerique[];
}

function getValueByDomaines(comps: CompetenceNumerique[]): ValueDomaine[] {
  return allDomaines.reduce((acc, dom) => {
    const compsDom = comps.filter(c =>
      dom.competencesNumerique.some(cd => cd.code === c.code),
    );
    if (compsDom.length > 0) {
      return [
        ...acc,
        {
          domaine: dom.libelle,
          competences: compsDom,
        },
      ];
    }
    return acc;
  }, []);
}

export function Competences(props: Props) {
  const { value, onChange, print, livretEvalId } = props;
  const remainingDomaines = getRemainingParcoursNomenclature(value);
  const { t } = useTranslation(['carnet', 'common']);
  const { updateLivretEvalCompNumerique } = useUpdateLivretEvalCompNumerique();
  const textareaRef = useRef();
  const domaineValues = getValueByDomaines(value);
  return (
    <div>
      {(domaineValues || []).map(d => (
        <div tw="mb-4" key={d.domaine}>
          <div tw="font-bold">{d.domaine}</div>
          {d.competences.map(c => {
            const matchingNomenclature = getAllCompetencesNumeriques().find(
              nom => nom.code === c.code,
            );
            const index = findIndex(value, v => v.code === c.code);
            return (
              <div key={c.code} tw="relative mb-1">
                <div tw="flex justify-between items-baseline">
                  <div css={[!print && tw`mb-0 text-sm`]}>
                    {matchingNomenclature?.libelle}
                  </div>
                  <div tw="flex">
                    {print ? (
                      <div>
                        {t('carnet:level')} {c.niveau}
                      </div>
                    ) : (
                      <Buttons<1 | 2 | 3 | undefined>
                        withoutMargin
                        small
                        onChange={niveau =>
                          onChange &&
                          onChange(
                            replaceItemAtIndex(value, { ...c, niveau }, index),
                          )
                        }
                        options={[
                          {
                            value: 1,
                            label: t('carnet:competenceNumericLevel', {
                              level: 1,
                            }),
                          },
                          {
                            value: 2,
                            label: t('carnet:competenceNumericLevel', {
                              level: 2,
                            }),
                          },
                          {
                            value: 3,
                            label: t('carnet:competenceNumericLevel', {
                              level: 3,
                            }),
                          },
                        ]}
                        value={c.niveau}
                      />
                    )}
                    {!print && onChange && (
                      <Menu>
                        <Menu.Button
                          minimal
                          tw="text-muted font-medium"
                          small
                          icon={DotsVertical}
                        />
                        <Menu.Items size="auto">
                          <Menu.Item
                            tw="whitespace-nowrap"
                            onClick={() =>
                              updateLivretEvalCompNumerique({
                                code: c.code,
                                niveau: c.niveau as number,
                                id: livretEvalId,
                              })
                            }
                            icon={DuplicateIcon as any}
                            text={t('carnet:livret.copyOnOtherStudents')}
                          />
                          <Menu.Item
                            tw="whitespace-nowrap"
                            onClick={() =>
                              onChange(removeItemAtIndex(value, index))
                            }
                            icon={Trash}
                            intent="danger"
                            text={t('common:buttons.delete')}
                          />
                        </Menu.Items>
                      </Menu>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ))}
      {!print && onChange && (
        <div css={[tw`text-center`, value.length > 0 && tw`mb-4`]}>
          <Menu large={value.length === 0}>
            <Menu.Button
              icon={Plus}
              fill
              extralarge={value.length === 0}
              minimal={value.length > 0}
            >
              {t('carnet:livret.addNumericalCompetence')}
            </Menu.Button>
            <Menu.Items fill limitHeight>
              {remainingDomaines.map(d => (
                <Fragment key={d.libelle}>
                  <div tw="px-4 py-2 font-bold">{d.libelle}</div>
                  {d.competencesNumerique.map(c => (
                    <Menu.Item
                      key={c.code}
                      onClick={() => {
                        onChange([...value, { code: c.code, niveau: 1 }]);
                        setTimeout(() => {
                          if (textareaRef?.current) {
                            (textareaRef.current as any)?.focus();
                          }
                        }, 100);
                      }}
                      text={c.libelle}
                    />
                  ))}
                </Fragment>
              ))}
            </Menu.Items>
          </Menu>
        </div>
      )}
    </div>
  );
}
